import { useEffect, useMemo, useState } from 'react';
import { Button, Input, Textarea, DataTable, Column, Badge, IconButton, useToast, ConfirmDialog, Modal, EmptyState, LongText } from '../../../shared/components';
import { PlusIcon, EditIcon, TrashIcon } from '../../../shared/icons';
import { getTurnoGeoRepository } from '../../../shared/services/turnoGeo/repositoryFactory';
import { Label } from '../../../shared/services/turnoGeo/types';
import { DEFAULT_LABELS, invalidateLabels, renderLabelText } from '../labels';

const repo = getTurnoGeoRepository();

const SAMPLE: Record<string, string> = {
  dia: 'Lunes', inicio: '08:00', fin: '17:24', almuerzo_sugerido: '12:00 – 13:00 o 13:00 – 14:00',
  distancia: '35', lugar: 'Oficina', radio: '100', modalidad: 'Oficina', precision: '12', maximo: '50',
};

/**
 * Parámetros > Etiquetas: mensajes y ayudas de TurnoGeo editables (ej. el almuerzo sugerido).
 * Las del sistema se pueden editar y desactivar (vuelve el texto por defecto) pero no eliminar.
 */
export function LabelsTab() {
  const toast = useToast();
  const [rows, setRows] = useState<Label[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editing, setEditing] = useState<Label | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: '', category: 'GENERAL', title: '', body: '', variables: '', sortOrder: '0' });
  const [isSaving, setIsSaving] = useState(false);
  const [deleting, setDeleting] = useState<Label | null>(null);
  const [category, setCategory] = useState('');

  const load = () => {
    setIsLoading(true);
    repo.listAllLabels().then((r) => { setRows(r); setIsLoading(false); }).catch((e) => { setIsLoading(false); toast.show(e instanceof Error ? e.message : 'No se pudieron cargar las etiquetas.', 'error'); });
  };
  useEffect(load, []);

  const categories = useMemo(() => Array.from(new Set(rows.map((r) => r.category))).sort(), [rows]);
  const shown = category ? rows.filter((r) => r.category === category) : rows;

  const openCreate = () => { setForm({ code: '', category: 'GENERAL', title: '', body: '', variables: '', sortOrder: String(rows.length + 1) }); setCreating(true); };
  const openEdit = (r: Label) => { setForm({ code: r.code, category: r.category, title: r.title, body: r.body, variables: r.variables ?? '', sortOrder: String(r.sortOrder) }); setEditing(r); };
  const close = () => { setCreating(false); setEditing(null); };

  const vars = form.variables.split(',').map((v) => v.trim().replace(/[{}]/g, '')).filter(Boolean);
  const insertVar = (v: string) => setForm((f) => ({ ...f, body: `${f.body}{${v}}` }));

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.show('Título y texto son obligatorios.', 'error'); return; }
    if (!editing && !form.code.trim()) { toast.show('El código es obligatorio.', 'error'); return; }
    setIsSaving(true);
    try {
      const payload = { category: form.category, title: form.title, body: form.body, variables: form.variables.trim() || null, sortOrder: Number(form.sortOrder) || 0 };
      if (editing) { await repo.updateLabel(editing.id, payload); toast.show('Etiqueta actualizada.'); }
      else { await repo.createLabel({ ...payload, code: form.code }); toast.show('Etiqueta creada.'); }
      invalidateLabels();
      close();
      load();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo guardar.', 'error');
    } finally { setIsSaving(false); }
  };

  const toggleActive = async (r: Label) => {
    try { await repo.updateLabel(r.id, { isActive: !r.isActive }); invalidateLabels(); load(); }
    catch (e) { toast.show(e instanceof Error ? e.message : 'No se pudo cambiar.', 'error'); }
  };
  const restoreDefault = () => {
    const d = editing && DEFAULT_LABELS[editing.code];
    if (d) setForm((f) => ({ ...f, body: d }));
  };

  const columns: Column<Label>[] = [
    { key: 'title', filterValue: (r) => r.title, header: 'Etiqueta', render: (r) => <span className="font-medium">{r.title}</span>, sortable: true, sortValue: (r) => r.title },
    { key: 'code', header: 'Código', render: (r) => <code className="text-xs">{r.code}</code> },
    { key: 'category', filterValue: (r) => r.category, header: 'Categoría', render: (r) => r.category, sortable: true, sortValue: (r) => r.category },
    { key: 'body', header: 'Texto', render: (r) => <LongText text={r.body} /> },
    {
      key: 'estado', header: 'Estado',
      render: (r) => (
        <button onClick={() => toggleActive(r)} title="Activar / desactivar">
          <Badge tone={r.isActive ? 'success' : 'neutral'}>{r.isActive ? 'Activa' : 'Inactiva (texto por defecto)'}</Badge>
        </button>
      ),
    },
    {
      key: 'actions', header: '',
      render: (r) => (
        <div className="flex gap-1">
          <IconButton icon={<EditIcon width={16} height={16} />} label="Editar" onClick={() => openEdit(r)} className="h-9 w-9 text-ink-500 hover:bg-ink-100" />
          {!r.isSystem && <IconButton icon={<TrashIcon width={16} height={16} />} label="Eliminar" onClick={() => setDeleting(r)} className="h-9 w-9 text-critical hover:bg-red-50" />}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button leftIcon={<PlusIcon width={18} height={18} />} onClick={openCreate}>Agregar etiqueta</Button>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-11 rounded-xl border border-ink-200 bg-surface px-3 text-sm text-ink-800">
          <option value="">Todas las categorías</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <p className="text-xs text-ink-500">Aquí editas los mensajes y ayudas que ven los empleados (por ejemplo el almuerzo sugerido). Usa <code>{'{variables}'}</code> para insertar datos automáticos.</p>

      {shown.length === 0 && !isLoading ? (
        <EmptyState icon="🏷️" title="Sin etiquetas" description="Agrega la primera etiqueta." />
      ) : (
        <DataTable excel fixedKey="title" columns={columns} rows={shown} rowKey={(r) => r.id} isLoading={isLoading} emptyTitle="Sin etiquetas" />
      )}

      <Modal open={creating || !!editing} onClose={close} title={editing ? 'Editar etiqueta' : 'Agregar etiqueta'}>
        <div className="space-y-3">
          <Input label="Código (interno, MAYÚSCULAS_Y_GUION_BAJO)" value={form.code} disabled={!!editing} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') })} />
          <Input label="Título" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <Input label="Categoría" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') })} />
          <Textarea label="Texto" rows={5} maxLength={1000} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
          <Input label="Variables disponibles (separadas por coma)" value={form.variables} onChange={(e) => setForm({ ...form, variables: e.target.value })} />
          {vars.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {vars.map((v) => (
                <button key={v} type="button" onClick={() => insertVar(v)} className="rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700">+ {`{${v}}`}</button>
              ))}
            </div>
          )}
          <div className="rounded-xl bg-ink-50 p-3 text-sm text-ink-700">
            <p className="mb-1 text-xs font-semibold uppercase text-ink-400">Vista previa</p>
            <p className="break-words">{renderLabelText(form.body, SAMPLE)}</p>
          </div>
          {editing && DEFAULT_LABELS[editing.code] && <Button variant="outline" onClick={restoreDefault}>Restaurar texto por defecto</Button>}
          <div className="flex gap-3 pt-2">
            <Button variant="outline" fullWidth onClick={close}>Cancelar</Button>
            <Button fullWidth onClick={save} isLoading={isSaving}>Guardar</Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        title="Eliminar etiqueta"
        description={`¿Eliminar "${deleting?.title}"?`}
        tone="danger"
        confirmLabel="Eliminar"
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (deleting) { try { await repo.deleteLabel(deleting.id); invalidateLabels(); } catch (e) { toast.show(e instanceof Error ? e.message : 'No se pudo eliminar.', 'error'); } }
          setDeleting(null);
          load();
        }}
      />
    </div>
  );
}
